'use client';

import { FC, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'root/src/client/modules/mui/hooks';
import { BannerPromoStoryblok } from 'storyblok-types';
import { PromoBanner } from 'pages/StoryBlok/PromoBanner';
import { usePopoverManager } from 'modules/shop/popoverManager/hooks';
import { PopoverType } from 'modules/shop/popoverManager/types';
import { useSiteConfig } from 'modules/shop/siteConfig/hooks';
import { INavigationItem } from 'modules/stroyblok/types';
import { ClientPageType } from 'src/app/types';
import { HEADER_ID } from 'src/client/components/constants';
import { HeaderLine, HeaderLineSearchField } from './HeaderLine';
import { Navigation } from './Navigation';
import { HeaderPopover } from './Navigation/HeaderPopover';
import { TopNotificationContainer } from './Navigation/TopNotification';
import { PageLoader } from './PageLoader';
import styles from './styles.module.css';

export type HeaderState = {
  visibleItem: INavigationItem | null;
  showMore: boolean;
  navDetailHeight: number;
  isSticky?: boolean;
};

export const Header: FC<{
  promoBanner: BannerPromoStoryblok | null;
  promoLogo?: { src?: string; alt?: string };
}> = ({ promoLogo, promoBanner }) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const mobileSearchFieldRef = useRef<HTMLDivElement>(null);
  const desktopNavRef = useRef<HTMLDivElement>(null);
  const [state, updateState] = useState<HeaderState>({
    visibleItem: null,
    navDetailHeight: 0,
    showMore: false,
  });
  const { isMobile } = useMediaQuery();
  const { gtmData } = useSiteConfig();
  const { activePopover } = usePopoverManager();

  const isPromoBanner = [
    ClientPageType.HOME,
    ClientPageType.CATEGORY,
    ClientPageType.PDP,
  ].includes(gtmData?.pageType as ClientPageType);
  const isFixedSearchBar = activePopover === PopoverType.SIDEBAR;

  useEffect(() => {
    const eventHandler = () => {
      let isSticky = false;

      if (isMobile && mobileSearchFieldRef.current) {
        isSticky = window.scrollY > mobileSearchFieldRef.current.clientHeight;
      } else if (headerRef.current && desktopNavRef.current) {
        isSticky =
          headerRef.current.getBoundingClientRect().bottom +
            desktopNavRef.current.clientHeight <=
          0;
      }

      updateState((s) => ({ ...s, isSticky }));
    };

    eventHandler();

    window.addEventListener('scroll', eventHandler);

    return () => {
      window.removeEventListener('scroll', eventHandler);
    };
  }, [mobileSearchFieldRef, desktopNavRef, headerRef]);

  return (
    <>
      <PageLoader />
      <header ref={headerRef} id={HEADER_ID} className={styles.header}>
        <HeaderLine
          promoLogo={promoLogo}
          state={state}
          hideSearchIcon={!state?.isSticky || isFixedSearchBar}
        />
      </header>
      {isMobile && (
        <HeaderLineSearchField
          ref={mobileSearchFieldRef}
          isFixed={isFixedSearchBar}
          style={{
            top: isFixedSearchBar
              ? `${headerRef?.current?.getBoundingClientRect().height}px`
              : 'unset',
          }}
        />
      )}
      <Navigation ref={desktopNavRef} state={state} updateState={updateState} />
      <HeaderPopover />
      <TopNotificationContainer />
      {isPromoBanner && promoBanner && <PromoBanner blok={promoBanner} />}
    </>
  );
};
